import { gql, useQuery } from '@apollo/client';
import React from 'react';
import { useHistory } from 'react-router';

import { HeaderTab } from 'src/components/common/headerTab/HeaderTab';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { PageLayout } from 'src/components/common/pageLayout/PageLayout';
import { PageTitle } from 'src/components/common/pageTitle/PageTitle';
import { DocumentTitle } from 'src/components/documentTitle/DocumentTitle';
import { PermissionBlockCard } from 'src/components/permissionGuards/permissionBlockCard/PermissionBlockCard';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { PlanProtectedArea } from 'src/components/planProtectedArea/PlanProtectedArea';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { useLDFlag } from 'src/lib/launchDarkly';

import { graphRefToString, useGraphRef } from '../hooks/useGraphRef';
import {
  cloudSettingsRouteConfig,
  variantHomepageRouteConfig,
} from '../routes';
import { NonPublicGraphArea } from '../variantLayout/NonPublicGraphArea';

import { CloudRoutingConfigurationPage } from './CloudRoutingConfigurationPage';
import { CloudRoutingSettingsPage } from './CloudRoutingSettingsPage';
import { EndpointURLsPage } from './EndpointURLsPage';

export const CloudSettingsLayout = () => {
  const history = useHistory();
  const graphRef = useGraphRef();
  const [currentAccountId] = useCurrentAccountId();
  const { tab = 'general' } = useRouteParams(cloudSettingsRouteConfig);
  const useFeatureFlagCustomDomains = useLDFlag('betelgeuse-custom-domains');

  // Early redirect if trying to access the endpoint-urls tab without the `useFeatureFlagCustomDomains` feature flag
  React.useEffect(() => {
    if (tab === 'endpoint-urls' && !useFeatureFlagCustomDomains) {
      history.push(
        variantHomepageRouteConfig.path({
          graphId: graphRef?.graphId || '',
          graphVariant: graphRef?.graphVariant || '',
        }),
      );
    }
  }, [useFeatureFlagCustomDomains, history, tab, graphRef]);

  const { loading } = useQuery<
    GraphQLTypes.CloudSettingsLayoutQuery,
    GraphQLTypes.CloudSettingsLayoutQueryVariables
  >(
    gql`
      query CloudSettingsLayoutQuery($graphId: ID!) {
        graph(id: $graphId) {
          graphType
          id
        }
      }
    `,
    {
      variables: {
        graphId: graphRef?.graphId as string,
      },
      skip: !graphRef,
    },
  );

  const ProtectedGraphArea: typeof PlanProtectedArea = (props) => (
    <PlanProtectedArea {...props} redirectExpiredToOrg={true} />
  );

  if (!graphRef) {
    return <NotFound />;
  }

  if (loading) {
    return (
      <div className="grid h-full place-items-center">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <PageLayout
      className="bg-primary"
      title={
        <PageTitle
          title="Cloud Router"
          tabs={
            <>
              <HeaderTab
                tabID="General"
                isActive={() => tab === 'general'}
                to={cloudSettingsRouteConfig.path({
                  ...graphRef,
                  tab: 'general',
                })}
              />
              {useFeatureFlagCustomDomains && (
                <HeaderTab
                  tabID="Endpoints"
                  isActive={() => tab === 'endpoint-urls'}
                  to={cloudSettingsRouteConfig.path({
                    ...graphRef,
                    tab: 'endpoint-urls',
                  })}
                />
              )}
              <HeaderTab
                tabID="Configuration"
                isActive={() => tab === 'configuration'}
                to={cloudSettingsRouteConfig.path({
                  ...graphRef,
                  tab: 'configuration',
                })}
              />
            </>
          }
        />
      }
    >
      <div className="grid w-full gap-4 py-4 pl-1 pr-4">
        <NonPublicGraphArea>
          <ProtectedGraphArea accountId={currentAccountId}>
            <DocumentTitle title="Cloud Router">
              <PermissionGuard
                graphId={graphRef.graphId}
                graphPermissions="canQueryStats"
                fallback={PermissionBlockCard}
              >
                {tab === 'general' ? (
                  <PermissionGuard
                    graphId={graphRef.graphId}
                    graphPermissions="canQueryStats"
                    fallback={PermissionBlockCard}
                  >
                    <CloudRoutingSettingsPage />
                  </PermissionGuard>
                ) : tab === 'endpoint-urls' ? (
                  <PermissionGuard
                    graphId={graphRef.graphId}
                    graphVariant={graphRef.graphVariant}
                    graphVariantPermissions="canManageCloudRouter"
                    fallback={PermissionBlockCard}
                  >
                    <EndpointURLsPage routerId={graphRefToString(graphRef)} />
                  </PermissionGuard>
                ) : (
                  tab === 'configuration' && (
                    <PermissionGuard
                      graphId={graphRef.graphId}
                      graphVariant={graphRef.graphVariant}
                      graphVariantPermissions="canManageCloudRouter"
                      fallback={PermissionBlockCard}
                    >
                      <CloudRoutingConfigurationPage />
                    </PermissionGuard>
                  )
                )}
              </PermissionGuard>
            </DocumentTitle>
          </ProtectedGraphArea>
        </NonPublicGraphArea>
      </div>
    </PageLayout>
  );
};
